import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sanitizeReferralCode } from '../lib/sanitizeReferralCode.ts';
import {
  enrichAccessTypesWithSoldOut,
  graphqlAccessQuery,
  graphqlTicketQuery,
  graphqlAddOnQuery,
  graphqlEventsByRestaurantsQuery,
  isTicketBookable,
  type BookingEvent,
} from '@/lib/api.ts';
import { getPublicGraphqlEndpoint, type PublicRequestableEvent } from '@/lib/rta-api.ts';

import {
  AvailabilityOption,
  AvailDate,
  MetaAvailability,
  RestaurantResponseData,
} from '@/lib/types.ts';
import {
  useWidgetState,
  useRestaurantId,
  UserAtom,
  AuthAtom,
  reloadRestaurantKey,
  widgetPaymentMethodDeselection,
} from '../lib/state.ts';
import { isPaymentReady } from '../lib/ready.ts';
import { getGuestLabel, isLegacyPriceLabel } from '../lib/eventFlow.ts';
import { track } from '@/lib/analytics/index.ts';
import { formatMinimumSpendDeposit, MINIMUM_SPEND_PER_PERSON_LABEL } from '@/lib/labels.ts';
import { useWidgetAnalytics } from '../lib/hooks/useWidgetAnalytics.ts';
import { useAnnounce } from '../lib/announcer.ts';
import NeedToKnows from '@/components/NeedToKnows/NeedToKnows.tsx';
import { RequestToAttendModal } from '../RequestToAttend/RequestToAttendModal.tsx';
import { RequestableZoneOptions } from '../RequestToAttend/RequestableZoneOptions.tsx';
import { useRequestToAttend } from '../RequestToAttend/useRequestToAttend.ts';
import { getDatePillText } from './eventDateRange.ts';
import VenueMap from './VenueMap.tsx';
import {
  buildEventVenueTargets,
  resolveSelectedTicketId,
  type AccessType,
  type EventVenueTarget,
  type TicketItem,
} from './eventVenueMap.ts';
import { MAP_SCROLL_GAP_PX, settleCardBelowStickyMap } from './venueMapScroll.ts';
import { stripVenueDescriptionMarkup } from '../../../lib/venueDescription.ts';
import styles from './EventPage.module.css';

interface AddOnItem {
  id: number;
  price: number;
  price__currency: string;
  sold_out: boolean;
  guests: number;
  maximum_purchasable: number;
  item: {
    name?: string;
    description?: string;
  };
}

interface EventPageProps {
  restaurant?: RestaurantResponseData | null;
  meta?: {
    availability: MetaAvailability[];
    dates?: AvailDate[];
  } | null;
}

const parseEventDate = (value?: string) => {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const sortEventsByDate = (a: BookingEvent, b: BookingEvent) => {
  const aDate = parseEventDate(a.date);
  const bDate = parseEventDate(b.date);

  if (aDate && bDate) {
    return aDate.valueOf() - bDate.valueOf();
  }

  if (aDate) {
    return -1;
  }

  if (bDate) {
    return 1;
  }

  return a.id - b.id;
};

// Events that cross midnight (e.g. nightlife starting 23:30 and ending 04:00
// the next day) should stay visible until their actual end time, not until
// the end of `event.date` in the browser's local timezone. Prefer
// `datetime_end_utc` when the API provides it — the `DateTimeTz` scalar
// emits ISO 8601 with an offset (e.g. "2026-04-25T11:00:00+00:00") which
// `dayjs()` parses to an absolute moment regardless of browser timezone.
// Fall back to the old end-of-day heuristic for events missing the field.
const isEventUpcoming = (event: BookingEvent, now: dayjs.Dayjs): boolean => {
  if (event.datetime_end_utc) {
    const endAt = dayjs(event.datetime_end_utc);
    if (endAt.isValid()) {
      return !endAt.isBefore(now);
    }
  }

  const eventDate = parseEventDate(event.date);
  return eventDate ? !eventDate.endOf('day').isBefore(now) : false;
};

const getDisplayableEvents = (rawEvents: BookingEvent[]): BookingEvent[] => {
  const now = dayjs();
  const upcoming = rawEvents
    .filter((event) => isEventUpcoming(event, now))
    .sort(sortEventsByDate);

  if (upcoming.length > 0) {
    return upcoming;
  }

  return [...rawEvents].sort(sortEventsByDate);
};

/** Guest icon SVG used in time and table cards */
const GuestIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 8a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM2 14.5c0-2.485 2.686-4.5 6-4.5s6 2.015 6 4.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

/** Location pin icon */
const LocationPin: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7 1C4.515 1 2.5 3.015 2.5 5.5 2.5 9.25 7 13 7 13s4.5-3.75 4.5-7.5C11.5 3.015 9.485 1 7 1z"
      fill="currentColor"
    />
    <circle cx="7" cy="5.5" r="1.5" fill="#fff" />
  </svg>
);


/**
 * EventPage - Screen 1 of the Coachella booking widget
 *
 * Displays event hero image, date/time/table selection cards,
 * and "Begin booking" CTA. Matches Figma node 1031:3703.
 */
const getClassicOptionKey = (option: AvailabilityOption) =>
  option.diff_key ?? `${option.seating_id}-${option.seats}-${option.price}`;

const CLASSIC_DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;

// A ?date= deeplink counts only when well-formed and bookable; otherwise the
// default auto-select should still pick the first day.
const isBookableDeeplinkDate = (
  raw: string | null | undefined,
  dates: { date: string }[]
): raw is string =>
  !!raw && CLASSIC_DATE_PARAM_RE.test(raw) && dates.some((d) => d.date === raw);

const EventPage: React.FC<EventPageProps> = ({ restaurant, meta }) => {
  const [searchParams] = useSearchParams();
  const id = useRestaurantId();
  const navigate = useNavigate();
  const [widget, updateWidget] = useWidgetState();
  const user = useAtomValue(UserAtom);
  const auth = useAtomValue(AuthAtom);
  const appliedReferralCodeRef = useRef<string | null>(null);
  // Deeplink applied-key tracking. The composite key is (restaurant id +
  // raw URL value) for each deeplink param. EventPage is reused across
  // SPA navigation between booking URLs (it doesn't remount), so a plain
  // boolean guard would silently skip subsequent deeplinks when the user
  // navigates between restaurants or between different `?event_id=` /
  // `?date=` values for the same restaurant. The composite key resets
  // implicitly whenever either input changes, while still preventing
  // unrelated effect re-runs (e.g. fetchEvents re-firing after the user
  // picks a ticket) from clobbering the user's in-session selection back
  // to the URL value.
  const lastAppliedEventIdKeyRef = useRef<string | null>(null);
  const lastAppliedDateKeyRef = useRef<string | null>(null);
  const user_ready = !!auth?.token && !!user?.phone;
  const code = sanitizeReferralCode(searchParams.get('code'));
  const urlEventIdRaw = searchParams.get('event_id');
  const urlDateRaw = searchParams.get('date');

  // Event data
  const [events, setEvents] = useState<BookingEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [accessTypes, setAccessTypes] = useState<AccessType[]>([]);
  // Every zone's ticket rows for the selected event, keyed by access-type id —
  // the ONE store for ticket rows: the venue map reads every zone, the ticket
  // list reads the expanded zone (ticketInventory, derived below). Seeded from
  // the per-zone fetch enrichAccessTypesWithSoldOut already makes; refreshed
  // per zone whenever that zone is expanded. The eventId tag guards against a
  // stale cache after an event switch.
  const [zoneInventory, setZoneInventory] = useState<{
    eventId: number;
    byAccessType: Record<number, TicketItem[]>;
  } | null>(null);
  const [selectedAccessType, setSelectedAccessType] = useState<AccessType | null>(null);
  // The expanded zone's rows, derived from zoneInventory so the list and the
  // map can never disagree. Empty when no zone is expanded or the cache
  // belongs to another event.
  const selectedAccessTypeId = selectedAccessType?.id ?? null;
  const ticketInventory = useMemo(
    () =>
      (selectedAccessTypeId !== null && zoneInventory?.eventId === selectedEventId
        ? zoneInventory.byAccessType[selectedAccessTypeId]
        : undefined) ?? [],
    [zoneInventory, selectedEventId, selectedAccessTypeId]
  );
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(
    widget.eventInfo?.ticketItemId ?? null
  );
  const [ticketQuantity, setTicketQuantity] = useState<number>(
    widget.eventInfo?.quantity ?? 1
  );
  const [addOns, setAddOns] = useState<AddOnItem[]>([]);
  const [addOnQuantities, setAddOnQuantities] = useState<Record<number, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [accessTypesLoading, setAccessTypesLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const payment_ready = user_ready && isPaymentReady(widget);
  // Single source of truth for "clear the paid selection" — the hook calls
  // this inside selectRequestableFlow whenever an RTA zone becomes
  // authoritative, so the whole paid-selection set is reset in one place
  // rather than split across the handler and this callback.
  // Clearing selectedAccessType also empties the derived ticketInventory; the
  // zone cache itself stays so the venue map keeps every zone's tables.
  const clearPaidSelection = useCallback(() => {
    setSelectedAccessType(null);
    setSelectedTicketId(null);
    setTicketsLoading(false);
  }, []);
  const {
    requestableZones,
    activeRequestableSelection,
    selectedRequestableRow,
    eventPrimaryAction,
    selectPaidFlow,
    selectRequestableFlow,
    beginPaidLoad,
    canApplyPaidLoad,
    canFinishPaidLoad,
  } = useRequestToAttend({
    restaurantId: restaurant?.id,
    selectedEventId,
    hasPaidInventory: ticketInventory.length > 0,
    onRequestableSelection: clearPaidSelection,
  });

  const [isPending, startTransition] = useTransition();

  // Classic availability fallback (REST API data rendered in new event UI)
  const classicDates = useMemo(() => {
    const dates = meta?.dates ?? [];
    return dates.filter((d) => d.amount_of_tables > 0 || d.allow_special_request);
  }, [meta?.dates]);

  const classicTimes = useMemo(() => {
    if (!widget.date) return [];
    return (meta?.availability ?? []).filter((slot) => slot.date === widget.date);
  }, [meta?.availability, widget.date]);

  const useClassicAvailability = !isLoading && events.length === 0 && classicDates.length > 0 && restaurant?.type !== 'event';

  const isClassicDateSoldOut = !!widget.date && !isLoading && !isPending && classicTimes.length === 0;

  const beginClassicBooking = useCallback(() => {
    if (!id || !restaurant || !widget.availability || !widget.option) return;

    track('Widget Continue to Details', { restaurant_id: id, booking_type: 'restaurant' });

    const dateRestaurant = dayjs(widget.availability.datetime_utc).tz(restaurant.timezone);
    const dateFormatted = dateRestaurant ? dateRestaurant.format('YYYY-MM-DD') : '';

    updateWidget({
      payment_error: undefined,
      bookingType: 'restaurant',
      // Clear stale event data so payment type detection uses restaurant config only
      event: undefined,
      eventInfo: undefined,
      confirm_body: {
        restaurant_id: id,
        seating_id: widget.option.seating_id,
        party: widget.option.seats,
        date: dateFormatted,
        time: widget.availability.time,
        skip_level_check: true,
      },
      referral_code: code,
    });

    if (payment_ready) {
      navigate('../payment');
      return;
    }

    if (user_ready) {
      navigate('../payment');
      return;
    }

    updateWidget(widgetPaymentMethodDeselection());
    navigate('../details');
  }, [
    id,
    restaurant,
    widget.availability,
    widget.option,
    updateWidget,
    code,
    payment_ready,
    user_ready,
    navigate,
  ]);

  // Select a classic date — wrapped in startTransition to keep the current
  // UI visible while useRestaurant() re-fetches availability for the new date.
  const handleClassicDateSelect = useCallback(
    (date: string) => {
      startTransition(() => {
        reloadRestaurantKey();
        updateWidget({
          date,
          // Scope the date to this restaurant so a stale date in global state resets.
          date_restaurant_id: id,
          availability: undefined,
          option: undefined,
          reservation_error: undefined,
        });
      });
    },
    [updateWidget, id]
  );

  // Deeplink: classic restaurants can be opened with ?date=YYYY-MM-DD to
  // pre-select a specific bookable date. Re-evaluates whenever the
  // composite (restaurant id + raw URL date) key changes, so SPA
  // navigation between booking URLs picks up new date deeplinks. Falls
  // through silently when the URL value is missing, malformed, already
  // selected, or not in the bookable list.
  useEffect(() => {
    const dateAppliedKey = `${id ?? ''}:${urlDateRaw ?? ''}`;
    if (lastAppliedDateKeyRef.current === dateAppliedKey) return;
    if (!useClassicAvailability) return;
    if (classicDates.length === 0) return;

    lastAppliedDateKeyRef.current = dateAppliedKey;

    if (!urlDateRaw || widget.date === urlDateRaw) return;
    // Strict format + bookable check (shared with the default auto-select) —
    // rejects permissive dayjs inputs like "2026/8/25".
    if (!isBookableDeeplinkDate(urlDateRaw, classicDates)) return;

    handleClassicDateSelect(urlDateRaw);
  }, [
    id,
    urlDateRaw,
    useClassicAvailability,
    classicDates,
    widget.date,
    handleClassicDateSelect,
  ]);

  // Auto-select the first bookable date; reset to the first day when the
  // restaurant changes (global widget state leaks the previous date). A valid
  // ?date= deeplink wins; an invalid/unbookable one falls through to the default.
  useEffect(() => {
    if (!useClassicAvailability || classicDates.length === 0) return;
    if (isBookableDeeplinkDate(urlDateRaw, classicDates)) return;
    // Act only once the loaded data is the current restaurant's (restaurant.id is
    // a string, id a number — coerce). Avoids picking the previous one mid-refetch.
    if (restaurant?.id == null || Number(restaurant.id) !== id) return;
    // Reset only when the date is known to belong to another restaurant; a
    // legacy/undefined scope (sessions predating this field, no version bump) is
    // kept if still bookable here, preserving a valid same-restaurant selection.
    const fromOtherRestaurant =
      widget.date_restaurant_id != null && widget.date_restaurant_id !== id;
    if (!fromOtherRestaurant && widget.date && classicDates.some((d) => d.date === widget.date)) {
      return;
    }
    const firstDate = classicDates.find((d) => d.amount_of_tables > 0) ?? classicDates[0];
    handleClassicDateSelect(firstDate.date);
  }, [
    useClassicAvailability,
    widget.date,
    widget.date_restaurant_id,
    urlDateRaw,
    classicDates,
    id,
    restaurant?.id,
    handleClassicDateSelect,
  ]);

  // Auto-select the first time + table once slots load. Keeps a still-offered
  // slot but refreshes it to the latest data (so a changed/sold-out table can't
  // be booked from a stale copy) and replaces selections left over from another
  // restaurant. Compared by value — meta is rebuilt each render, so comparing by
  // object identity would always differ and loop.
  useEffect(() => {
    if (!useClassicAvailability || !widget.date || classicTimes.length === 0) {
      return;
    }
    const currentSlot = widget.availability;
    const slot = (currentSlot && classicTimes.find((s) => s.uid === currentSlot.uid)) ?? classicTimes[0];
    const currentOption = widget.option;
    const optionValid =
      !!currentOption &&
      slot.options.some((o) => getClassicOptionKey(o) === getClassicOptionKey(currentOption));
    const optionsKey = (s: MetaAvailability) => s.options.map(getClassicOptionKey).join('|');
    const slotUnchanged =
      !!currentSlot && currentSlot.uid === slot.uid && optionsKey(currentSlot) === optionsKey(slot);
    if (slotUnchanged && optionValid) {
      return;
    }
    updateWidget({
      availability: slot,
      option: optionValid ? currentOption : slot.options[0],
      reservation_error: undefined,
    });
  }, [useClassicAvailability, widget.date, widget.availability, widget.option, classicTimes, updateWidget]);

  // Hero image
  const [heroIndex, setHeroIndex] = useState(0);
  const heroImages = restaurant?.wide_crop_image_url ? [restaurant.wide_crop_image_url] : [];

  const [venueMapFailed, setVenueMapFailed] = useState(false);
  // Reset the failed flag on restaurant change (useState setter bails when unchanged).
  useEffect(() => {
    setVenueMapFailed(false);
  }, [restaurant?.id, restaurant?.venue_map_url]);
  const venueMapUrl = !venueMapFailed && restaurant?.venue_map_url ? restaurant.venue_map_url : null;
  // venue_map_interactive lives in widget state so the payment page (no map) reads the
  // same signal. It must survive this page unmounting on nav to payment, so the reset
  // can't live in VenueMap's unmount cleanup — clear it here when this page has no map.
  useEffect(() => {
    if (!venueMapUrl && widget.venue_map_interactive) {
      updateWidget({ venue_map_interactive: false });
    }
  }, [venueMapUrl, widget.venue_map_interactive, updateWidget]);
  // In interactive-map mode, access-type/ticket prices read as a minimum-spend deposit.
  const withDepositLabel = (price: string): string =>
    widget.venue_map_interactive ? formatMinimumSpendDeposit(price) : price;

  // Filter out unbookable rows (tier-aware, see isTicketBookable) so the map
  // can't highlight an unselectable section.
  const selectableTickets = ticketInventory.filter(isTicketBookable);
  const selectedTicket = selectableTickets.find((ticket) => ticket.id === selectedTicketId) ?? null;

  // Map-shape candidates: every access type and every cached zone's tickets
  // (see buildEventVenueTargets).
  const eventVenueTargets = useMemo(
    () => buildEventVenueTargets({ accessTypes, zoneInventory, selectedEventId }),
    [accessTypes, zoneInventory, selectedEventId],
  );

  // Highlight the selected access type AND/OR ticket — only the level that maps
  // to a shape lights up, so passing both is safe for either config.
  const eventSelectedTargets = useMemo<EventVenueTarget[]>(() => {
    const targets: EventVenueTarget[] = [];
    if (selectedAccessType) {
      targets.push({ kind: 'access', name: selectedAccessType.name, acc: selectedAccessType });
    }
    if (selectedTicket && selectedAccessType) {
      targets.push({
        kind: 'ticket',
        name: selectedTicket.item?.name ?? '',
        ticket: selectedTicket,
        acc: selectedAccessType,
      });
    }
    return targets;
  }, [selectedAccessType, selectedTicket]);

  // After a map tap, scroll the selected card into view (it's often hidden below
  // the sticky map). mapSelectionRef marks selections that came from the map.
  const selectedCardRef = useRef<HTMLElement | null>(null);
  const mapSelectionRef = useRef(false);

  // Keep the deep-linked selected date pill visible within its horizontal strip.
  const dateStripRef = useRef<HTMLDivElement | null>(null);
  const selectedDatePillRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!mapSelectionRef.current) {
      return;
    }
    // In the new event config a map tap selects the access type, which then
    // async-loads its tickets and auto-selects the first one. The access-type
    // card renders FIRST (before tickets load), so scrolling now would land on
    // it. Wait for tickets to finish loading so selectedCardRef points at the
    // auto-selected ticket card (rendered last) — the deepest selection.
    if (ticketsLoading) {
      return;
    }
    mapSelectionRef.current = false;
    const card = selectedCardRef.current;
    if (!card) {
      return;
    }
    // Land the card just below the pinned map; center as a fallback if the map
    // element isn't found (so it can't end up behind a sticky header).
    // Scope to the card's root node so this resolves inside the embed's shadow
    // root (where a document-level query would find nothing); equivalent to
    // `document` in the SPA.
    const root = card.getRootNode() as Document | ShadowRoot;
    const stickyMap = root.querySelector('[data-venue-map-root]');
    if (stickyMap instanceof HTMLElement) {
      // scroll-margin-top aligns the card's top below the pinned map; the smooth
      // scroll can still race late layout (ticket rows/images render after
      // ticketsLoading flips), so settleCardBelowStickyMap re-aligns on observed
      // layout changes until the member's first scroll input (or its short
      // backstop). Its cancel function is the effect cleanup.
      card.style.scrollMarginTop = `${stickyMap.offsetHeight + MAP_SCROLL_GAP_PX}px`;
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return settleCardBelowStickyMap(card, stickyMap);
    }
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // selectedAccessType drives the scroll for the new config (map shape →
    // access type); selectedTicketId / widget.option for the legacy + classic.
  }, [widget.option, selectedTicketId, selectedAccessType?.id, ticketsLoading]);

  // Bring the selected date pill into view inside its horizontal strip when a
  // deep-linked ?event_id= pre-selects a date that's scrolled off-screen. Only
  // the strip scrolls horizontally — the page's vertical scroll stays on the
  // selected ticket card (handled above). No-ops when the pill is already fully
  // visible, so tapping a visible date doesn't cause a jump.
  useEffect(() => {
    const strip = dateStripRef.current;
    const pill = selectedDatePillRef.current;
    if (selectedEventId == null || !strip || !pill) {
      return;
    }
    const stripRect = strip.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    const fullyVisible = pillRect.left >= stripRect.left && pillRect.right <= stripRect.right;
    if (fullyVisible) {
      return;
    }
    // Center the pill within the strip.
    const delta = pillRect.left - stripRect.left - (strip.clientWidth - pill.clientWidth) / 2;
    strip.scrollBy({ left: delta, behavior: 'smooth' });
    // events (stable state), not events.length — re-reveal on same-count item swaps too.
  }, [selectedEventId, events]);

  // Fetch events and pick the default-selected event. Re-runs whenever
  // any input to the selection logic changes (restaurant, auth, the URL
  // ?event_id= primitive, or persisted widget event refs) so the closure
  // never reads stale URL values. The composite-key ref above prevents
  // unchanged keys from re-applying the URL on top of the user's
  // in-session selection.
  useEffect(() => {
    if (!restaurant?.id) return;

    const fetchEvents = async () => {
      try {
        const eventInfo = await graphqlEventsByRestaurantsQuery(restaurant.id, auth?.token ?? null);
        const normalizedEvents = (eventInfo?.data?.getEventsByRestaurantId || [])
          .filter(Boolean)
          .map((event): BookingEvent | null => {
            const normalizedId = Number(event.id);
            if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
              return null;
            }

            return {
              ...event,
              id: normalizedId,
            };
          })
          .filter((event): event is BookingEvent => event !== null);
        const displayEvents = getDisplayableEvents(normalizedEvents);

        setEvents(displayEvents);
        if (displayEvents.length > 0) {
          // Deeplink priority: URL ?event_id= (only when its composite key
          // hasn't been applied yet) > persisted selection in widget state
          // > first event. Falls through silently when the URL value is
          // missing, malformed, or doesn't match an available event, so
          // external links never break.
          const eventIdAppliedKey = `${id ?? ''}:${urlEventIdRaw ?? ''}`;
          const isFreshUrlEventId =
            lastAppliedEventIdKeyRef.current !== eventIdAppliedKey;
          const parsedUrlEventId = urlEventIdRaw ? Number(urlEventIdRaw) : NaN;
          // Number.isInteger (rather than isFinite) skips obviously-invalid
          // inputs like "3.5" or "1e2" before the displayEvents.some scan.
          const urlEventId =
            isFreshUrlEventId &&
            Number.isInteger(parsedUrlEventId) &&
            parsedUrlEventId > 0
              ? parsedUrlEventId
              : null;

          const persistedEventId = widget.event?.id ?? widget.eventInfo?.id;

          let nextSelectedEventId: number;
          if (urlEventId && displayEvents.some((event) => event.id === urlEventId)) {
            nextSelectedEventId = urlEventId;
          } else if (
            persistedEventId &&
            displayEvents.some((event) => event.id === persistedEventId)
          ) {
            nextSelectedEventId = persistedEventId;
          } else {
            nextSelectedEventId = displayEvents[0].id;
          }

          // Mark this composite key as applied regardless of which branch
          // we took (no-match, persisted, or first) so re-runs of this
          // effect after the user picks a ticket don't reset the user's
          // in-session selection back to the URL value. SPA navigation
          // that changes ?event_id= produces a new composite key here, so
          // the URL value re-applies for the new key.
          lastAppliedEventIdKeyRef.current = eventIdAppliedKey;

          setSelectedEventId(nextSelectedEventId);
        } else {
          setSelectedEventId(null);
          setAccessTypes([]);
          setZoneInventory(null);
          setSelectedTicketId(null);
        }
        setIsLoading(false);
      } catch (err) {
        Sentry.captureException(err);
        setSelectedEventId(null);
        setAccessTypes([]);
        setZoneInventory(null);
        setSelectedTicketId(null);
        setIsLoading(false);
      }
    };

    void fetchEvents();
  }, [
    restaurant?.id,
    auth?.token,
    widget.event?.id,
    widget.eventInfo?.id,
    id,
    urlEventIdRaw,
  ]);

  // Fetch access types when event changes
  // Resolves to the load's outcome: 'applied' (rows + selection written),
  // 'failed' (threw or returned no data while still the latest paid load), or
  // 'superseded' (a newer paid load, RTA pick, or event switch owns state now).
  const loadTicketsForAccessType = useCallback(
    async (
      eventId: number,
      accessTypeId: number,
      selectTicketId?: number,
    ): Promise<'applied' | 'failed' | 'superseded'> => {
      const paidLoadToken = beginPaidLoad();
      setTicketsLoading(true);
      try {
        const result = await graphqlTicketQuery(eventId, accessTypeId, auth?.token ?? null);
        if (result?.data && canApplyPaidLoad(paidLoadToken)) {
          const inventory = (result.data.ticketInventory || []).filter(Boolean);
          // The single write for this zone's rows — the list (derived
          // ticketInventory) and the map both read it. Inside the apply gate,
          // so a superseded load can't overwrite fresher rows. Passing the gate
          // means `eventId` is still the current event (an event switch bumps
          // the guard revision), so a null or other-event `prev` is the stale
          // one: seed a fresh cache rather than discard these rows.
          setZoneInventory((prev) => ({
            eventId,
            byAccessType: {
              ...(prev?.eventId === eventId ? prev.byAccessType : {}),
              [accessTypeId]: inventory,
            },
          }));
          const persistedTicketId =
            widget.eventInfo?.id === eventId ? widget.eventInfo?.ticketItemId : null;
          const nextTicketId = resolveSelectedTicketId({
            inventory,
            selectTicketId,
            persistedTicketId,
          });
          setSelectedTicketId(nextTicketId);
          // Reset quantity like handleTicketSelect does — otherwise a raised
          // quantity from the previous access type leaks into the new ticket.
          // A map tap always resets; a restored persisted ticket keeps it.
          const autoSelected = nextTicketId != null && nextTicketId !== persistedTicketId;
          if (selectTicketId != null || autoSelected) {
            setTicketQuantity(1);
          }
          if (selectTicketId != null && nextTicketId == null && id != null) {
            // The tapped table sold out between render and refetch.
            track('Widget Table Selected', {
              restaurant_id: id,
              booking_type: 'event',
              event_id: eventId,
              access_type_id: accessTypeId,
              seating_id: selectTicketId,
              source: 'map',
              outcome: 'sold_out_on_refetch',
            });
          }
          return 'applied';
        }
        if (!canApplyPaidLoad(paidLoadToken)) {
          return 'superseded';
        }
        // Resolved without data while still the latest load: the caller may
        // collapse the list, so report it (never on 'superseded' — normal).
        Sentry.captureMessage('Event zone ticket load returned no data', {
          level: 'warning',
          extra: { eventId, accessTypeId, selectTicketId: selectTicketId ?? null },
        });
        if (id != null) {
          track('Widget Zone Load Failed', {
            restaurant_id: id,
            booking_type: 'event',
            event_id: eventId,
            access_type_id: accessTypeId,
            outcome: 'no_data',
          });
        }
        return 'failed';
      } catch (err) {
        Sentry.captureException(err, {
          extra: { eventId, accessTypeId, selectTicketId: selectTicketId ?? null },
        });
        return canApplyPaidLoad(paidLoadToken) ? 'failed' : 'superseded';
      } finally {
        if (canFinishPaidLoad(paidLoadToken)) {
          setTicketsLoading(false);
        }
      }
    },
    [
      auth?.token,
      beginPaidLoad,
      canApplyPaidLoad,
      canFinishPaidLoad,
      id,
      widget.eventInfo?.id,
      widget.eventInfo?.ticketItemId,
    ]
  );

  useEffect(() => {
    if (!selectedEventId) {
      setIsLoading(false);
      return;
    }

    setSelectedAccessType(null);
    setAccessTypes([]);
    setZoneInventory(null);
    setSelectedTicketId(null);
    setAccessTypesLoading(true);
    setTicketsLoading(true);

    const fetchAccessTypes = async () => {
      try {
        const result = await graphqlAccessQuery(selectedEventId, auth?.token ?? null);
        if (result?.data) {
          let availableAccessTypes = (result.data.accessTypes || []).filter(Boolean);

          // Derive sold_out for each access type from its ticket inventory,
          // and keep the rows that fetch already loaded as the venue map's
          // all-zone inventory cache (see eventVenueTargets).
          const enriched = await enrichAccessTypesWithSoldOut(
            selectedEventId,
            availableAccessTypes,
            auth?.token ?? null,
          );
          availableAccessTypes = enriched.accessTypes;

          setAccessTypes(availableAccessTypes);
          setZoneInventory({
            eventId: selectedEventId,
            byAccessType: enriched.inventoryByAccessType,
          });
          setAccessTypesLoading(false);
          if (availableAccessTypes.length > 0) {
            const first = availableAccessTypes.find((at) => !at.sold_out) ?? availableAccessTypes[0];
            setSelectedAccessType(first);
            if (!first.sold_out) {
              await loadTicketsForAccessType(selectedEventId, first.id);
            } else {
              setTicketsLoading(false);
            }
          } else {
            setTicketsLoading(false);
          }
        } else {
          setAccessTypesLoading(false);
          setTicketsLoading(false);
        }
      } catch (err) {
        Sentry.captureException(err);
        setAccessTypesLoading(false);
        setTicketsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchAccessTypes();
  }, [selectedEventId, auth?.token, loadTicketsForAccessType]);

  // "Type" (vs "Select Type") only when there's exactly one option AND it is
  // actually selected. Read the answer from selection state rather than
  // re-deriving the fetch's auto-select rule — the fetch picks
  // `find(!sold_out) ?? [0]`, which a count-based predicate can't reproduce
  // without drifting (it ignores coming_soon, and the `?? [0]` fallback even
  // auto-selects a sold-out single type). Requestable zones only become
  // selected via an explicit click, so an RTA-only event keeps "Select".
  const hasAutoSelectedType =
    accessTypes.length + requestableZones.length === 1 && selectedAccessType !== null;

  // Expand a zone and load its tickets. `selectTicketId` (a map tap on a
  // specific table) selects exactly that ticket once the zone's rows land.
  const handleAccessTypeClick = useCallback(
    async (acc: AccessType, selectTicketId?: number) => {
      if (acc.sold_out || acc.coming_soon) return;
      selectPaidFlow();
      // Captured before the switch so a failed load can roll back to it.
      const previousAccessType = selectedAccessType;
      const previousTicketId = selectedTicketId;
      setSelectedAccessType(acc);
      if (!selectedEventId) return;
      const outcome = await loadTicketsForAccessType(selectedEventId, acc.id, selectTicketId);
      // A zone switch whose fetch never landed would leave the header on the
      // new zone while selectedTicketId still names the previous zone's
      // ticket — beginBooking could then book the wrong zone's table. Restore
      // the previous zone + ticket (its rows did land). Only on 'failed':
      // 'superseded' means a newer selection owns state and must not be undone.
      if (outcome === 'failed' && previousAccessType?.id !== acc.id) {
        setSelectedAccessType(previousAccessType);
        setSelectedTicketId(previousTicketId);
      }
    },
    [selectPaidFlow, selectedAccessType, selectedTicketId, selectedEventId, loadTicketsForAccessType]
  );

  // Selecting a requestable (RTA) zone from the shared Select Type list clears
  // any paid access-type selection and its loaded tiers, so paid and RTA remain
  // mutually exclusive within the single selection group.
  const handleRequestableSelect = useCallback(
    (row: PublicRequestableEvent) => {
      // Paid selection is cleared by the hook (selectRequestableFlow →
      // clearPaidSelection); this handler only makes the row authoritative
      // and records the funnel event.
      selectRequestableFlow(row);
      if (id === null) return;
      track('Widget RTA Selected', {
        restaurant_id: id,
        booking_type: 'event',
        event_id: row.event_id,
        event_inventory_id: row.event_inventory_id,
        zone: row.inventory_name ?? undefined,
      });
    },
    [id, selectRequestableFlow]
  );

  const persistSelection = useCallback(
    (ticketItem: TicketItem) => {
      if (!selectedEventId || !id) {
        return false;
      }

      const selectedEvent = events.find((event) => event.id === selectedEventId);

      // Build add-on item IDs array: each add-on ID repeated by its selected quantity
      const addOnItemIds: number[] = [];
      for (const [addonId, qty] of Object.entries(addOnQuantities)) {
        for (let i = 0; i < qty; i++) {
          addOnItemIds.push(Number(addonId));
        }
      }

      updateWidget({
        confirm_body: {
          restaurant_id: id,
          seating_id: ticketItem.id,
          party: ticketItem.guests,
          date: selectedEvent?.date ?? dayjs().format('YYYY-MM-DD'),
          time: selectedEvent?.time ?? '00:00',
        },
        date: selectedEvent?.date ?? dayjs().format('YYYY-MM-DD'),
        option: {
          price__currency: ticketItem.price__currency,
          price: ticketItem.price,
          seats: ticketItem.guests,
          seating_id: ticketItem.id,
          type: null,
        },
        event: {
          id: selectedEventId,
        },
        eventInfo: {
          id: selectedEventId,
          ticketItemId: ticketItem.id,
          quantity: ticketQuantity,
          allowedPaymentTypes: ticketItem.allowedPaymentTypes ?? [],
          addOnItemIds,
          requires_idv: ticketItem.item?.requires_idv === true,
        },
      });

      return true;
    },
    [selectedEventId, id, events, updateWidget, ticketQuantity, addOnQuantities]
  );

  // Fetch add-ons when ticket selection changes.
  // Restores persisted quantities from widget.eventInfo.addOnItemIds when
  // navigating back to this page so the user's earlier selections survive.
  useEffect(() => {
    if (!selectedTicketId) {
      setAddOns([]);
      setAddOnQuantities({});
      return;
    }

    let stale = false;

    const fetchAddOns = async () => {
      try {
        const result = await graphqlAddOnQuery(selectedTicketId, auth?.token ?? null);
        if (stale) return;
        if (result?.data?.addOnInventory) {
          const inventory = result.data.addOnInventory.filter(Boolean);
          setAddOns(inventory);

          // Restore persisted add-on quantities from widget state (survives navigation)
          const persisted = widget.eventInfo?.addOnItemIds;
          if (persisted && persisted.length > 0) {
            const restored: Record<number, number> = {};
            for (const id of persisted) {
              restored[id] = (restored[id] ?? 0) + 1;
            }
            // Only restore quantities for add-ons that still exist in inventory
            const validIds = new Set(inventory.map((a) => a.id));
            const filtered: Record<number, number> = {};
            for (const [id, qty] of Object.entries(restored)) {
              if (validIds.has(Number(id))) {
                filtered[Number(id)] = qty;
              }
            }
            setAddOnQuantities(filtered);
          } else {
            setAddOnQuantities({});
          }
        } else {
          setAddOns([]);
          setAddOnQuantities({});
        }
      } catch (err) {
        if (stale) return;
        Sentry.captureException(err);
        setAddOns([]);
        setAddOnQuantities({});
      }
    };

    void fetchAddOns();

    return () => {
      stale = true;
    };
  }, [selectedTicketId, auth?.token]);

  const handleTicketSelect = useCallback(
    (ticketItem: TicketItem) => {
      if (!isTicketBookable(ticketItem)) {
        return;
      }
      selectPaidFlow();
      setSelectedTicketId(ticketItem.id);
      setTicketQuantity(1);
    },
    [selectPaidFlow],
  );

  // Map tap on a table: tables in another (or no) expanded zone switch to that
  // zone and select that exact ticket; a table in the loaded zone is a plain
  // select. A table is tappable iff its inventory row exists and is not sold
  // out — its zone never has to be the expanded one.
  const handleMapTicketSelect = (ticket: TicketItem, acc: AccessType) => {
    if (acc.sold_out || acc.coming_soon || !isTicketBookable(ticket)) {
      return;
    }
    // Arm the map-anchored scroll (and record the pick) only once a selection
    // actually commits, and only when it changes — an armed flag with no
    // selection change would hijack the next unrelated selection's scroll.
    if (ticket.id !== selectedTicketId) {
      mapSelectionRef.current = true;
      if (id != null) {
        track('Widget Table Selected', {
          restaurant_id: id,
          booking_type: 'event',
          seating_id: ticket.id,
          party_size: ticket.guests,
          ...(selectedEventId != null && { event_id: selectedEventId }),
          access_type_id: acc.id,
          source: 'map',
          cross_zone: acc.id !== selectedAccessTypeId,
        });
      }
    }
    // `!ticketsLoading`: the derived list shows the zone's cached rows while
    // its fetch is in flight — a plain select then would be overwritten by
    // that fetch's auto-select, so route through a ticket-targeted load.
    if (
      acc.id === selectedAccessTypeId &&
      !ticketsLoading &&
      ticketInventory.some((row) => row.id === ticket.id)
    ) {
      handleTicketSelect(ticket);
      return;
    }
    void handleAccessTypeClick(acc, ticket.id);
  };

  // List pick: records the selection as `source: 'list'` (the map's
  // denominator). Kept out of handleTicketSelect, which the map's loaded-zone
  // path also calls, so a map tap never double-emits.
  const handleListTicketSelect = (ticket: TicketItem) => {
    if (ticket.id !== selectedTicketId && isTicketBookable(ticket) && id != null) {
      track('Widget Table Selected', {
        restaurant_id: id,
        booking_type: 'event',
        seating_id: ticket.id,
        party_size: ticket.guests,
        ...(selectedEventId != null && { event_id: selectedEventId }),
        ...(selectedAccessTypeId != null && { access_type_id: selectedAccessTypeId }),
        source: 'list',
      });
    }
    handleTicketSelect(ticket);
  };

  const beginBooking = useCallback(() => {
    // `selectedTicket` is drawn from the bookable rows only, so a restored id
    // whose table has since sold out can never be persisted and booked.
    if (!id || !selectedTicket || !persistSelection(selectedTicket)) {
      return;
    }

    track('Widget Continue to Details', { restaurant_id: id, booking_type: 'event' });

    updateWidget({ bookingType: 'event' });

    if (user_ready) {
      navigate('../payment');
      return;
    }

    updateWidget(widgetPaymentMethodDeselection());
    navigate('../details');
  }, [id, selectedTicket, persistSelection, user_ready, updateWidget, navigate]);

  // Referral code handling
  useEffect(() => {
    if (!code || appliedReferralCodeRef.current === code) {
      return;
    }
    appliedReferralCodeRef.current = code;
    updateWidget({ referral_code: code });
  }, [code, updateWidget]);

  // Announce loaded availability to assistive tech (no-op in the SPA). Fires
  // once slots settle for the current date/event; the announcer dedupes so
  // re-renders and same-count date swaps don't repeat. Skipped mid-transition
  // (`isPending`) so a stale count isn't announced before the new one.
  const announce = useAnnounce();
  useEffect(() => {
    if (isLoading || isPending) return;
    if (useClassicAvailability) {
      if (!widget.date) return;
      if (isClassicDateSoldOut) {
        announce('No availability for the selected date');
        return;
      }
      const n = classicTimes.length;
      if (n > 0) announce(`${n} time${n === 1 ? '' : 's'} available`);
    } else if (ticketInventory.length > 0) {
      // Announce only the SELECTABLE tickets (sold-out / coming-soon are rendered
      // unavailable and rejected by handleTicketSelect), so SR users don't hear
      // "1 option available" for an event whose only ticket is Sold Out.
      const n = selectableTickets.length;
      announce(n > 0 ? `${n} option${n === 1 ? '' : 's'} available` : 'No availability for this event');
    }
  }, [
    isLoading,
    isPending,
    useClassicAvailability,
    isClassicDateSoldOut,
    classicTimes.length,
    ticketInventory.length,
    selectableTickets.length,
    widget.date,
    announce,
  ]);

  // Top-of-funnel analytics (`Widget Loaded` / `Widget Schedule Viewed`).
  useWidgetAnalytics({
    restaurant,
    code,
    urlEventIdRaw,
    isLoading,
    hasEvents: events.length > 0,
    hasClassicDates: classicDates.length > 0,
    date: widget.date,
  });

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.eventInfo}>
          <div className={styles.loadingCard} style={{ width: '60%', height: 28 }} />
          <div className={styles.loadingCard} style={{ width: '40%', height: 20, marginTop: 8 }} />
        </div>
        <div className={styles.selectionSection}>
          <div className={styles.cardRow}>
            <div className={styles.loadingCard} />
            <div className={styles.loadingCard} />
            <div className={styles.loadingCard} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Hero Image (hidden when a venue-map URL is configured for the restaurant) */}
      {!venueMapUrl && heroImages.length > 0 && (
        <div className={styles.heroSection}>
          <div className={styles.heroImageWrapper}>
            <img
              src={heroImages[heroIndex]}
              alt={restaurant?.name ?? 'Event hero'}
              className={styles.heroImage}
            />
            <div className={styles.heroOverlay} aria-hidden="true" />
            {restaurant?.transparent_logo_image_url && (
              <img
                src={restaurant.transparent_logo_image_url}
                className={styles.transparentLogo}
                alt={`${restaurant.name} logo`}
              />
            )}
          </div>
          {heroImages.length > 1 && (
            <div className={styles.heroPagination}>
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  className={clsx(styles.heroDot, i === heroIndex && styles.heroDotActive)}
                  onClick={() => setHeroIndex(i)}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Venue map — replaces the hero when the restaurant has a venue layout
          configured. Shapes map to the bookable sections of whichever flow is
          active: event tickets (`ticket.item.name`) or classic table options
          (`option.type`). VenueMap auto-detects interactive SVG vs static
          image. Matching is slug-based (case + space/_/- insensitive). */}
      {venueMapUrl && (
        useClassicAvailability ? (
          <VenueMap
            imageUrl={venueMapUrl}
            imageAlt={`${restaurant?.name ?? 'Venue'} layout`}
            items={widget.availability?.options ?? []}
            // The classic availability list is not pre-filtered, so every
            // option stays selectable (unchanged behavior).
            isItemAvailable={() => true}
            selectedItems={widget.option ? [widget.option] : []}
            getItemSectionName={(option) => option.type}
            onSelectItem={(option) => {
              // Flag a scroll only when the selection changes (avoids a stale flag).
              const changed =
                !widget.option ||
                getClassicOptionKey(option) !== getClassicOptionKey(widget.option);
              if (changed) {
                mapSelectionRef.current = true;
              }
              updateWidget({ option, reservation_error: undefined });
            }}
            onLoadError={() => setVenueMapFailed(true)}
          />
        ) : (
          <VenueMap
            imageUrl={venueMapUrl}
            imageAlt={`${restaurant?.name ?? 'Venue'} layout`}
            items={eventVenueTargets}
            // A section is bookable when its zone is open and, for a ticket,
            // the ticket itself is (a ticket in a coming-soon zone stays dimmed).
            isItemAvailable={(target) =>
              !target.acc.sold_out &&
              !target.acc.coming_soon &&
              (target.kind === 'access' || isTicketBookable(target.ticket))
            }
            selectedItems={eventSelectedTargets}
            getItemSectionName={(target) => target.name}
            onSelectItem={(target) => {
              // Flag a scroll only when the selection changes (avoids a stale flag).
              if (target.kind === 'access') {
                if (target.acc.id !== selectedAccessType?.id) {
                  mapSelectionRef.current = true;
                }
                void handleAccessTypeClick(target.acc);
              } else {
                // Arms its own scroll flag once the selection commits.
                handleMapTicketSelect(target.ticket, target.acc);
              }
            }}
            onLoadError={() => setVenueMapFailed(true)}
            onInteractiveChange={(interactive) => updateWidget({ venue_map_interactive: interactive })}
          />
        )
      )}

      {/* Event Details Card - contains title, selections, and CTA */}
      <div className={styles.eventInfo}>
        <div className={styles.eventHeaderInfo}>
          <div className={styles.eventTitleRow}>
            <h1 className={styles.eventTitle}>{restaurant?.name}</h1>
            {restaurant?.type === 'event' && (
              <span className={styles.eventBadge}>EVENT</span>
            )}
          </div>

          {restaurant?.address && (
            <div className={styles.eventLocation}>
              <LocationPin className={styles.locationPin} />
              <span>{restaurant.neighborhood_name || restaurant.address.split('\n')[0]}</span>
            </div>
          )}

          {restaurant?.medium_description && (
            <p className={styles.aboutText} style={{ color: '#666' }}>
              {restaurant.medium_description}
            </p>
          )}

          {widget?.reservation_error && (
            <div className={styles.errorMessage}>{widget.reservation_error}</div>
          )}
        </div>

        {/* Empty state — no events AND no classic availability */}
        {!isLoading && events.length === 0 && !useClassicAvailability && (
          <div className={styles.selectionSection}>
            <p
              style={{
                fontFamily: "'Sailec', sans-serif",
                fontSize: 16,
                color: '#666',
                textAlign: 'center',
                padding: '20px 0',
              }}
            >
              {restaurant?.type === 'event'
                ? 'No events are currently available. Please check back soon.'
                : 'No availability found. Please check back soon.'}
            </p>
          </div>
        )}

        {/*
          a11y (HOS-1832): these section titles stay `<h3>` under the page `<h1>`.
          `.eventPageWrapper :global(h2)` (EventPage.module.css) is an `!important`
          rule that would force any `<h2>` here to 24px/500/no-margin, overriding
          the non-`!important` `.sectionTitle`/`.aboutTitle`/etc. classes and
          visually regressing BOTH the SPA and the embed. Sequential heading-level
          ordering (h1→h2) is therefore deferred as a MODERATE axe item — it needs
          a shared-CSS change, out of scope for this a11y-hardening pass.
        */}
        {/* Classic availability — Select a date (rendered in new event UI cards) */}
        {useClassicAvailability && (
          <div className={styles.selectionSection}>
            <h3 className={styles.sectionTitle}>Select a date</h3>
            <div className={styles.cardRow}>
              {classicDates.map((dateEntry) => {
                const isSelected = widget.date === dateEntry.date;
                return (
                  <button
                    key={dateEntry.date}
                    className={isSelected ? styles.dateCardSelected : styles.dateCard}
                    onClick={() => {
                      if (!isSelected && id != null) {
                        track('Widget Date Selected', {
                          restaurant_id: id,
                          booking_type: 'restaurant',
                          date: dateEntry.date,
                        });
                      }
                      handleClassicDateSelect(dateEntry.date);
                    }}
                  >
                    <div className={styles.dateCardTitle}>
                      {dayjs(dateEntry.date).format('ddd, MMM D')}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={clsx(styles.availabilitySections, isPending && styles.sectionLoading)} aria-busy={isPending}>
          {/* Classic availability — Select a time */}
          {useClassicAvailability && !!widget.date && classicTimes.length > 0 && (
            <div className={styles.selectionSection}>
              <h3 className={styles.sectionTitle}>Select a time</h3>
              <div className={styles.cardRow}>
                {classicTimes.map((slot) => {
                  const isSelected = widget.availability?.uid === slot.uid;
                  return (
                    <button
                      key={slot.uid}
                      className={isSelected ? styles.timeCardSelected : styles.timeCard}
                      onClick={() => {
                        const autoOption =
                          slot.options.length === 1 ? slot.options[0] : undefined;
                        if (!isSelected && id != null) {
                          track('Widget Time Selected', {
                            restaurant_id: id,
                            booking_type: 'restaurant',
                            seating_id: autoOption?.seating_id,
                            party_size: autoOption?.seats,
                          });
                        }
                        updateWidget({
                          availability: slot,
                          option: autoOption,
                          reservation_error: undefined,
                        });
                      }}
                    >
                      <div className={styles.timeCardTime}>{slot.datetime_label}</div>
                      {slot.price > 0 && (
                        <div className={styles.timeCardPrice}>
                          {isLegacyPriceLabel(id)
                            ? <>{slot.price__currency} / person</>
                            : <>{slot.price__currency}<br />{MINIMUM_SPEND_PER_PERSON_LABEL}</>}
                        </div>
                      )}
                      <div className={styles.timeCardCapacity}>
                        <GuestIcon />
                        <span>
                          {slot.min_seats === slot.max_seats
                            ? `${slot.min_seats}`
                            : `${slot.min_seats}-${slot.max_seats}`}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Classic availability — Select a table */}
          {useClassicAvailability &&
            !!widget.availability &&
            widget.availability.options.length > 0 && (
              <div className={styles.selectionSection}>
                <h3 className={styles.sectionTitle}>Select a table</h3>
                <div className={styles.cardRow}>
                  {widget.availability.options.map((option) => {
                    const optionKey = getClassicOptionKey(option);
                    const isSelected =
                      (widget.option && getClassicOptionKey(widget.option) === optionKey) ?? false;
                    return (
                      <button
                        key={optionKey}
                        ref={(el) => {
                          if (isSelected) {
                            selectedCardRef.current = el;
                          }
                        }}
                        className={isSelected ? styles.tableCardSelected : styles.tableCard}
                        onClick={() => {
                          // Explicit user pick only — the auto-select effect
                          // updates widget.option directly and never reaches
                          // this handler, so it can't fire this event.
                          if (!isSelected && id != null) {
                            track('Widget Table Selected', {
                              restaurant_id: id,
                              booking_type: 'restaurant',
                              seating_id: option.seating_id,
                              party_size: option.seats,
                            });
                          }
                          updateWidget({
                            option,
                            reservation_error: undefined,
                          });
                        }}
                      >
                        <div className={styles.tableCardGuests}>
                          <GuestIcon />
                          <span>{option.seats}</span>
                        </div>
                        {option.price > 0 && (
                          <div className={styles.tableCardPrice}>
                            {isLegacyPriceLabel(id)
                              ? <>{option.price__currency} / person</>
                              : <>{option.price__currency}<br />{MINIMUM_SPEND_PER_PERSON_LABEL}</>}
                          </div>
                        )}
                        {!!option.type && <div className={styles.tableCardType}>{option.type}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        {/* Classic availability — Begin booking CTA */}
        {useClassicAvailability && (
          <div className={styles.ctaSection}>
            <button
              className={styles.ctaButton}
              disabled={!widget.option || !widget.availability}
              onClick={beginClassicBooking}
            >
              {isClassicDateSoldOut ? 'Public Reservations Sold Out' : 'Begin booking'}
            </button>

            {/* Show download app banner */}
            <section className={styles.appPromoBanner} aria-label="Download Dorsia app">
              <div className={styles.appPromoBannerContent}>
                <span className={styles.appPromoBannerLabel}>SECURE YOUR SPOT</span>
                <span className={styles.appPromoBannerText}>
                  More tables available<br />
                  on the Dorsia App
                </span>
              </div>
              <a
                className={styles.appPromoBannerButton}
                href="https://apps.apple.com/us/app/dorsia-members-club/id1589267208"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Dorsia
              </a>
            </section>
          </div>
        )}

        {/* Select a date (from GraphQL events). When only one option exists
            it's auto-selected, so the headers drop the "Select" prefix
            ("Date" / "Type") — there's nothing left to select. */}
        {events.length > 0 && (
          <div className={styles.selectionSection}>
            <h3 className={styles.sectionTitle}>
              {events.length === 1 ? 'Date' : 'Select a date'}
            </h3>
            <div ref={dateStripRef} className={styles.cardRow}>
              {events.map((event) => {
                const isSelected = event.id === selectedEventId;
                const { title, label } = getDatePillText(event);
                return (
                  <button
                    key={event.id}
                    ref={(el) => {
                      if (isSelected) {
                        selectedDatePillRef.current = el;
                      }
                    }}
                    className={isSelected ? styles.dateCardSelected : styles.dateCard}
                    onClick={() => setSelectedEventId(event.id)}
                  >
                    {title && <div className={styles.dateCardTitle}>{title}</div>}
                    <div className={title ? styles.dateCardSubtitle : styles.dateCardTitle}>
                      {label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Type (access types / requestable zones). The loading skeleton
            hardcodes "Select Type": while loading, accessTypes is always empty
            (the fetch clears it and sets loading together), so nothing is
            auto-selected yet and the "Type" arm would be unreachable. */}
        {accessTypesLoading && (
          <div className={styles.selectionSection}>
            <h3 className={styles.sectionTitle}>Select Type</h3>
            <div className={styles.accessTypeList}>
              <div className={styles.loadingCard} />
              <div className={styles.loadingCard} />
            </div>
          </div>
        )}
        {!accessTypesLoading && (accessTypes.length > 0 || requestableZones.length > 0) && (
          <div className={styles.selectionSection}>
            <h3 className={styles.sectionTitle}>
              {hasAutoSelectedType ? 'Type' : 'Select Type'}
            </h3>
            <div className={styles.accessTypeList}>
              {accessTypes.map((acc) => {
                const isSelected = selectedAccessType?.id === acc.id;
                return (
                  <button
                    key={acc.id}
                    ref={(el) => {
                      if (isSelected) {
                        selectedCardRef.current = el;
                      }
                    }}
                    className={isSelected ? styles.accessTypeCardSelected : styles.accessTypeCard}
                    onClick={() => handleAccessTypeClick(acc)}
                    disabled={acc.sold_out || acc.coming_soon}
                  >
                    <div className={styles.accessTypeName}>{acc.name}</div>
                    {acc.description && (
                      <div className={styles.accessTypeDescription}>{acc.description}</div>
                    )}
                    {acc.price_summary && (
                      <div className={styles.accessTypeInfo}>
                        {withDepositLabel(acc.price_summary)}
                      </div>
                    )}
                    {acc.sold_out && (
                      <div className={styles.accessTypeSoldOut}>Sold Out</div>
                    )}
                    {acc.coming_soon && !acc.sold_out && (
                      <div className={styles.accessTypeComingSoon}>Coming Soon</div>
                    )}
                  </button>
                );
              })}
              {/* Requestable (RTA) zones share the Select Type group with paid
                  access types — one unified, mutually exclusive selection. */}
              <RequestableZoneOptions
                rows={requestableZones}
                selectedInventoryId={selectedRequestableRow?.event_inventory_id ?? null}
                onSelect={handleRequestableSelect}
              />
            </div>
          </div>
        )}

        {/* Ticket inventory */}
        {ticketsLoading && (
          <div className={styles.selectionSection}>
            <div className={styles.ticketList}>
              <div className={styles.loadingCard} />
              <div className={styles.loadingCard} />
            </div>
          </div>
        )}
        {!ticketsLoading && ticketInventory.length > 0 && (
          <div className={styles.selectionSection}>
            <h3 className={styles.sectionTitle}>
              {selectedAccessType?.name ?? 'Select Quantity'}
            </h3>
            <div className={styles.ticketList}>
              {ticketInventory.map((ticket) => {
                const isSelected = selectedTicketId === ticket.id;
                const isComingSoon = !!ticket.item?.coming_soon;
                const isUnavailable = !isTicketBookable(ticket);
                // A tiered row's own sold_out can lag its tiers, so the badge
                // follows bookability; sold_out still wins over Coming Soon.
                const isSoldOut = isUnavailable && (ticket.sold_out || !isComingSoon);
                const showQuantityStepper = isSelected && !isUnavailable && ticket.maximum_purchasable > 1;
                return (
                  <div
                    key={ticket.id}
                    ref={(el) => {
                      if (isSelected) {
                        selectedCardRef.current = el;
                      }
                    }}
                    className={showQuantityStepper ? styles.ticketCardWithQty : (isSelected ? styles.ticketCardSelected : styles.ticketCard)}
                    onClick={() => handleListTicketSelect(ticket)}
                    role="button"
                    tabIndex={isUnavailable ? -1 : 0}
                    aria-disabled={isUnavailable}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleListTicketSelect(ticket);
                      }
                    }}
                  >
                    <div>
                      {ticket.item?.name && (
                        <div className={styles.ticketName}>{ticket.item.name}</div>
                      )}
                      <div className={styles.ticketGuests}>
                        {getGuestLabel(ticket.guests, id)}
                      </div>
                      {ticket.item?.description && (
                        <div className={styles.ticketDescription}>{ticket.item.description}</div>
                      )}
                      <div className={styles.ticketPrice}>
                        {withDepositLabel(ticket.price__currency)}
                      </div>
                      {isSoldOut && <div className={styles.ticketSoldOut}>Sold Out</div>}
                      {isComingSoon && !isSoldOut && (
                        <div className={styles.ticketComingSoon}>Coming Soon</div>
                      )}
                    </div>
                    {showQuantityStepper && (
                      <div className={styles.qtyStepper}>
                        <button
                          className={styles.qtyButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTicketQuantity((prev) => Math.max(1, prev - 1));
                          }}
                          disabled={ticketQuantity <= 1}
                        >
                          −
                        </button>
                        <span className={styles.qtyLabel}>{ticketQuantity}</span>
                        <button
                          className={styles.qtyButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTicketQuantity((prev) => Math.min(prev + 1, ticket.maximum_purchasable));
                          }}
                          disabled={ticketQuantity >= ticket.maximum_purchasable}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add-ons for selected ticket */}
        {selectedTicketId && addOns.length > 0 && (
          <div className={styles.selectionSection}>
            <h3 className={styles.sectionTitle}>Add-ons</h3>
            <div className={styles.ticketList}>
              {addOns.map((addon) => {
                const qty = addOnQuantities[addon.id] ?? 0;
                return (
                  <div
                    key={addon.id}
                    className={styles.ticketCard}
                    style={{ opacity: addon.sold_out ? 0.5 : 1 }}
                  >
                    <div>
                      {addon.item?.name && (
                        <div className={styles.ticketName}>{addon.item.name}</div>
                      )}
                      {addon.item?.description && (
                        <div className={styles.ticketDescription}>{addon.item.description}</div>
                      )}
                      <div className={styles.ticketGuests}>
                        {getGuestLabel(addon.guests, id)}
                      </div>
                      <div className={styles.ticketPrice}>{addon.price__currency}</div>
                      {addon.sold_out && (
                        <div className={styles.ticketSoldOut}>Sold Out</div>
                      )}
                    </div>
                    {!addon.sold_out && (
                      <div className={styles.qtyStepper}>
                        <button
                          className={styles.qtyButton}
                          onClick={() =>
                            setAddOnQuantities((prev) => {
                              const next = { ...prev };
                              if ((next[addon.id] ?? 0) > 0) {
                                next[addon.id] = (next[addon.id] ?? 0) - 1;
                                if (next[addon.id] === 0) delete next[addon.id];
                              }
                              return next;
                            })
                          }
                          disabled={qty === 0}
                        >
                          −
                        </button>
                        <span className={styles.qtyLabel}>{qty}</span>
                        <button
                          className={styles.qtyButton}
                          onClick={() =>
                            setAddOnQuantities((prev) => ({
                              ...prev,
                              [addon.id]: Math.min((prev[addon.id] ?? 0) + 1, addon.maximum_purchasable),
                            }))
                          }
                          disabled={qty >= addon.maximum_purchasable}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* The canonical RTA selection replaces Begin booking. Paid inventory
            retains the existing action when no request row is active. */}
        {eventPrimaryAction !== 'none' && (
          <div className={styles.ctaSection}>
            {eventPrimaryAction === 'request-to-attend' && activeRequestableSelection ? (
              <RequestToAttendModal
                endpoint={getPublicGraphqlEndpoint()}
                restaurantId={id}
                selection={activeRequestableSelection}
              />
            ) : (
              <button
                className={styles.ctaButton}
                disabled={!selectedTicket}
                onClick={beginBooking}
              >
                Begin booking
              </button>
            )}
          </div>
        )}

        {/* Custom request CTA (feature-flagged restaurants only) */}
        {restaurant?.features?.WidgetCustomRequest && restaurant?.special_request_days && (
          restaurant.special_request_days.sun ||
          restaurant.special_request_days.mon ||
          restaurant.special_request_days.tue ||
          restaurant.special_request_days.wed ||
          restaurant.special_request_days.thu ||
          restaurant.special_request_days.fri ||
          restaurant.special_request_days.sat
        ) && (
          <div className={styles.customRequestCard}>
            <div className={styles.customRequestCardInner}>
              <div className={styles.customRequestCardContent}>
                <h3 className={styles.customRequestCardTitle}>
                  <svg className={styles.customRequestSparkle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <g clipPath="url(#sparkles-clip)">
                      <path d="M17.7695 12.5001C18.0035 12.5001 18.2059 12.6626 18.257 12.8909L18.9006 15.7621C18.9664 16.0557 19.1196 16.3223 19.3391 16.5279C19.4911 16.6702 19.6709 16.7801 19.8674 16.849L21.1476 17.2979C21.3395 17.3651 21.4718 17.5423 21.4818 17.7453C21.4917 17.9481 21.3779 18.1366 21.1939 18.2225L19.7217 18.9089C19.6509 18.9419 19.5823 18.9804 19.5173 19.0235C19.18 19.2474 18.9422 19.5931 18.8537 19.9882L18.257 22.6481C18.2059 22.8765 18.0035 23.039 17.7695 23.039C17.5355 23.039 17.3332 22.8765 17.282 22.6481L16.615 19.6761C16.5643 19.4502 16.4463 19.2451 16.2759 19.0884C16.1765 18.9971 16.0612 18.924 15.9362 18.873L14.3673 18.2322C14.17 18.1515 14.0461 17.9547 14.0579 17.7419C14.0697 17.529 14.2147 17.3473 14.4198 17.2889L15.6965 16.9264C15.9593 16.8518 16.1912 16.6936 16.3574 16.4768C16.4692 16.3308 16.5485 16.1626 16.5887 15.9831L17.282 12.8909C17.3332 12.6626 17.5355 12.5001 17.7695 12.5001Z" stroke="currentColor" strokeLinejoin="round" />
                      <path d="M8.72909 1.50005C8.95833 1.49942 9.1588 1.65485 9.21522 1.87708L10.2524 5.9609C10.3885 6.49701 10.6352 6.99875 10.9768 7.43381C11.4082 7.98322 11.977 8.40954 12.6258 8.66849L14.1235 9.2658C14.3038 9.33775 14.4265 9.50778 14.4377 9.70153C14.4489 9.89519 14.3466 10.0781 14.176 10.1704L12.17 11.2525C11.812 11.4457 11.4853 11.6924 11.2012 11.9837C10.7099 12.4877 10.3592 13.1125 10.1847 13.7943L9.21453 17.5847C9.15778 17.8064 8.95798 17.9616 8.72909 17.961C8.50001 17.9602 8.30088 17.8035 8.24571 17.5812L7.25273 13.5775C7.11565 13.0248 6.84943 12.5121 6.47657 12.0818C6.16804 11.7257 5.79247 11.4334 5.37171 11.2214L3.29804 10.1766C3.12055 10.0872 3.01227 9.90137 3.02321 9.70291C3.03415 9.50442 3.16217 9.33104 3.34845 9.26166L4.93944 8.66918C5.63095 8.4116 6.2276 7.94919 6.64989 7.34404C6.90541 6.97784 7.09144 6.56782 7.19817 6.13423L8.24502 1.88054C8.29985 1.65775 8.49965 1.5009 8.72909 1.50005Z" stroke="currentColor" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="sparkles-clip">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  Submit a Custom Request
                </h3>
                <p className={styles.customRequestCardDescription}>
                  Specify your preferred times, party size, and table type. The restaurant will
                  review and respond within 24-48 hours. You&apos;ll only be charged if your
                  request is accepted.
                </p>
              </div>
              <div className={styles.customRequestCardAction}>
                <button
                  className={styles.customRequestButton}
                  onClick={() => navigate('../request')}
                >
                  Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* About the room */}
      {restaurant?.long_description && (
        <div className={styles.aboutSection}>
          <h3 className={styles.aboutTitle}>About the room</h3>
          <p className={styles.aboutText}>
            {stripVenueDescriptionMarkup(restaurant.long_description)}
          </p>
        </div>
      )}

      {/* Need to know */}
      {restaurant?.need_to_knows && restaurant.need_to_knows.length > 0 && (
        <div className={styles.needToKnowSection}>
          <h3 className={styles.needToKnowTitle}>Need to know</h3>
          <Suspense fallback={<div>Loading...</div>}>
            <NeedToKnows need_to_knows={restaurant.need_to_knows} />
          </Suspense>
        </div>
      )}
    </div>
  );
};

export default EventPage;
